import {useMemo} from 'react';
import {useIsAccountingModeResolved} from '../feature-flags/useIsAccountingModeResolved';
import {useIsAccountingExportMode} from '../feature-flags/useIsAccountingExportMode';
import {accountingTabKeysConstants, journalEntryStatusesConstants, suggestionRecordTypeConstants} from '../../constants/accounting.constants';

const {ERP_BILLS, ERP_VENDORS, NOT_ON_ERP, READY_TO_SYNC, SYNCED} = accountingTabKeysConstants;
const {JOURNAL_ENTRY} = suggestionRecordTypeConstants;
const {READY_FOR_REVIEW, READY_FOR_SUBMISSION} = journalEntryStatusesConstants;

/**
 * Tabs that exist only because NetSuite does.
 *
 * ERP_VENDORS and ERP_BILLS list records pulled *from* NetSuite, which cannot exist without the
 * integration. READY_TO_SYNC is unreachable rather than empty: nothing in export mode moves a row
 * to `ready_for_submission`, because Register replaces the ready-to-sync step entirely.
 */
const HIDDEN_IN_EXPORT_MODE = [ERP_BILLS, ERP_VENDORS, READY_TO_SYNC];

/**
 * What the surviving tabs are called, and what they ask the API for.
 *
 * `sync_status` is the only filter that means the same thing on all three pages. Journal entries
 * otherwise filter by `status`, which vendors and bills do not have at all.
 *
 * ⚠ `sync_status=all` returns ZERO rows — it does not mean "everything", despite the name, and the
 * published enum still lists only `all|none` even though `success` is live and filters correctly.
 * Both measured against dev, 2026-08-14 and re-measured 2026-08-17.
 */
const EXPORT_MODE_TABS = {
  [NOT_ON_ERP]: {labelKey: 'new', query: {sync_status: 'none'}},
  [SYNCED]: {labelKey: 'registered', query: {sync_status: 'success'}},
};

/**
 * Journal entries partition by `status` as well, because they have a stage the other two objects
 * do not: a row still missing its documents belongs under Missing Bills, not under New.
 *
 * Sent as an array, which the params serializer emits as repeated `status=` — the form the API
 * unions. ⚠ Do NOT let this become `status[]=`: that form is accepted and SILENTLY IGNORED, so the
 * tab would quietly show every row with no error anywhere.
 */
const JOURNAL_ENTRY_TAB_STATUS = {
  [NOT_ON_ERP]: [READY_FOR_REVIEW, READY_FOR_SUBMISSION],
};

/**
 * Mode-aware tab configuration for the three accounting pages.
 *
 * Each page owns its own tab keys, labels and query builder, so this deliberately returns pieces
 * rather than rendering anything: which tabs survive, what to call them, and what to send. The
 * mapping itself lives in one place because getting it inconsistent across the three pages is the
 * failure this is meant to prevent.
 *
 * In NetSuite mode every value passes through untouched — a company without the flag sees exactly
 * what it sees today, which is the acceptance criterion that matters most here.
 *
 * @param {Object} params
 * @param {string[]} params.tabKeys - The page's full tab list, in display order.
 * @param {string} params.defaultTabKey - The tab the page opens on.
 * @returns {{isExportMode: boolean, tabKeys: string[], defaultTabKey: string,
 *   labelKey: (key: string) => string, queryFor: (key: string) => Object}}
 *
 * @example
 * const {tabKeys, labelKey, queryFor} = useAccountingModeTabs({tabKeys: tabKeysArr, defaultTabKey});
 */
export const useAccountingModeTabs = ({tabKeys, defaultTabKey, recordType}) => {
  const isExportMode = useIsAccountingExportMode();
  // Exposed so a page can hold its tab strip back until the mode is known. Rendering first paints
  // NetSuite's tab names at a Manual ERP company and then swaps them (H1MVP-4791) — visible on any
  // direct URL or hard refresh, where the settings request has not landed yet.
  const isModeResolved = useIsAccountingModeResolved();

  const visibleTabKeys = useMemo(
    () => (isExportMode ? tabKeys.filter(key => !HIDDEN_IN_EXPORT_MODE.includes(key)) : tabKeys),
    [isExportMode, tabKeys],
  );

  // Journal entries open on Missing Bills, which survives; but if a page's default were ever a
  // hidden tab the page would open on a tab that does not exist, so fall back to the first survivor.
  const activeDefault = visibleTabKeys.includes(defaultTabKey) ? defaultTabKey : visibleTabKeys[0];

  return {
    isExportMode,
    isModeResolved,
    tabKeys: visibleTabKeys,
    defaultTabKey: activeDefault,

    /** The `viewOptions.<key>` suffix to translate — renamed only in export mode. */
    labelKey: (key) => (isExportMode && EXPORT_MODE_TABS[key]?.labelKey) || key,

    /**
     * The query overrides for a tab, or null to keep the page's own NetSuite-mode query.
     *
     * Journal entries get a status filter on top of sync_status, so the three tabs partition
     * exactly: a row still missing its documents belongs under Missing Bills, not New. That needs
     * two statuses in one request, which the API gained on 2026-08-18 — before that, New had to
     * fall back to sync_status alone and Missing Bills rows appeared twice.
     *
     * ⚠ Comma-separated values still 422; only repeated `status=` params union.
     */
    queryFor: (key) => {
      if (!isExportMode) return null;
      const query = EXPORT_MODE_TABS[key]?.query;
      if (!query) return null;
      if (recordType !== JOURNAL_ENTRY) return query;

      const status = JOURNAL_ENTRY_TAB_STATUS[key];
      return status ? {...query, status} : query;
    },
  };
};
